import React, { useState, useEffect } from 'react';
import promotionsService from '../services/promotionsService';
import storeService from '../services/storeService'; // <-- ADD THIS IMPORT
import { useAuth } from '../context/AuthContext';
import { FiEdit, FiTrash2 } from 'react-icons/fi';
import PromotionForm from '../components/promotions/PromotionForm'; // <-- ADD THIS IMPORT

const PromotionsPage = () => {
  const [promotions, setPromotions] = useState([]);
  const [stores, setStores] = useState([]); // <-- ADD THIS STATE
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const { user } = useAuth();

  const canRead = user?.permissions?.includes('promotions:read');
  const canCreate = user?.permissions?.includes('promotions:create');
  const canUpdate = user?.permissions?.includes('promotions:update');
  const canDelete = user?.permissions?.includes('promotions:delete');

  useEffect(() => {
    if (canRead) {
      // Fetch all necessary data in parallel
      Promise.all([
        promotionsService.getPromotions(),
        storeService.getStores()
      ]).then(([promosData, storesData]) => {
        setPromotions(promosData);
        setStores(storesData);
      }).catch(() => setError('Failed to load required data.'));
    }
  }, [canRead]);

  const handleOpenModal = (promo = null) => {
    setSuccessMessage('');
    setEditingPromotion(promo);
    setModalError('');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setEditingPromotion(null);
    setModalError('');
    setIsModalOpen(false);
  };

  const handleSavePromotion = async (promoData) => {
    if (isSaving) return;
    setIsSaving(true);
    setModalError('');
    try {
      if (editingPromotion) {
        await promotionsService.updatePromotion(editingPromotion._id, promoData);
      } else {
        await promotionsService.createPromotion(promoData);
      }
      const data = await promotionsService.getPromotions();
      setPromotions(data);
      handleCloseModal();
      setSuccessMessage('Promotion saved successfully.');
    } catch (err) {
      setModalError(err.response?.data?.message || 'Failed to save promotion.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePromotion = async (id) => {
    if (window.confirm('Are you sure you want to delete this promotion?')) {
      try {
        await promotionsService.deletePromotion(id);
        setPromotions(promotions.filter(p => p._id !== id));
      } catch (err) {
        setError('Failed to delete promotion.');
      }
    }
  };

  if (!canRead) {
    return (
        <div className="p-6">
            <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
            <p className="mt-2">You do not have the required permissions to view promotions.</p>
        </div>
    );
  }

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Promotions Management</h1>
        {canCreate && <button onClick={() => handleOpenModal()} className="flex items-center bg-primary text-white px-4 py-2 rounded-md shadow hover:bg-primary-dark">Add New Promotion</button>}
      </div>
      {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
      {successMessage && <p role="status" className="bg-green-100 text-green-800 p-3 rounded mb-4">{successMessage}</p>}
      <div className="bg-white p-4 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold mb-4">Existing Promotions</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">Name</th>
                <th className="px-6 py-3 text-left">Type</th>
                <th className="px-6 py-3 text-left">Status</th>
                {(canUpdate || canDelete) && <th className="px-6 py-3 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {promotions.map(promo => (
                <tr key={promo._id}>
                  <td className="px-6 py-4">{promo.name}</td>
                  <td className="px-6 py-4 font-mono text-xs">{promo.type.replace(/_/g, ' ')}</td>
                  <td className="px-6 py-4"><span className={`px-2 inline-flex text-xs font-semibold rounded-full ${promo.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{promo.isActive ? 'Active' : 'Inactive'}</span></td>
                  {(canUpdate || canDelete) && (
                    <td className="px-6 py-4 text-right space-x-4">
                      {canUpdate && <button onClick={() => handleOpenModal(promo)} className="text-indigo-600"><FiEdit size={18} /></button>}
                      {canDelete && <button onClick={() => handleDeletePromotion(promo._id)} className="text-red-600"><FiTrash2 size={18} /></button>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {isModalOpen && 
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">{editingPromotion ? 'Edit Promotion' : 'Add New Promotion'}</h2>
            {modalError && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{modalError}</p>}
            <PromotionForm 
              promotion={editingPromotion} 
              stores={stores}
              onSave={handleSavePromotion} 
              onCancel={handleCloseModal} 
              isSaving={isSaving}
            />
          </div>
        </div>
      }
    </div>
  );
};

export default PromotionsPage;
